import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { Gem, CalendarDays, Wallet, Sparkles } from "lucide-react";

const CountUp = ({ to, prefix = "", suffix = "", duration = 1.4 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(mv, to, { duration, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setVal(Math.round(v)) });
    return () => c.stop();
  }, [inView, to, mv, duration]);
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {val.toLocaleString("id-ID")}
      {suffix}
    </span>
  );
};

export const ValueCalculator = () => (
  <section className="py-20 md:py-28 bg-white relative overflow-hidden" id="kalkulator">
    <div className="absolute top-1/4 -right-32 w-[420px] h-[420px] rounded-full bg-blue-300/15 blur-[110px] pointer-events-none" />
    <div className="absolute bottom-0 -left-32 w-[360px] h-[360px] rounded-full bg-cyan-300/15 blur-[100px] pointer-events-none" />
    <div className="relative max-w-3xl mx-auto px-5 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="text-center mb-10"
      >
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
          Kalkulator Nilai Harian
        </h2>
        <p className="mt-4 text-slate-600 text-base md:text-lg">Lihat sendiri betapa ringannya biaya per harinya.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 36, scale: 0.97 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ y: -6 }}
        className="rounded-[24px] bg-white/60 backdrop-blur-2xl border border-white/70 shadow-[0_20px_60px_rgba(37,99,235,0.14)] hover:shadow-[0_30px_80px_rgba(37,99,235,0.22)] transition-shadow duration-500 p-7 md:p-10"
        data-testid="value-calculator-card"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-[0_8px_24px_rgba(37,99,235,0.35)]">
            <Gem className="w-6 h-6 text-white" strokeWidth={2.2} />
          </span>
          <h3 className="font-heading font-extrabold text-xl md:text-2xl text-slate-900">Gemini Pro 18 Bulan</h3>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 md:gap-5">
          <div className="rounded-2xl bg-white/70 backdrop-blur border border-slate-200/70 p-5">
            <div className="flex items-center gap-2 text-slate-500 mb-1.5">
              <Wallet className="w-4 h-4" strokeWidth={2.2} />
              <p className="text-xs font-bold uppercase tracking-[0.14em]">Harga Paket</p>
            </div>
            <p className="font-heading font-extrabold text-3xl md:text-4xl text-slate-900" data-testid="calc-price">
              <CountUp to={79000} prefix="Rp" />
            </p>
          </div>
          <div className="rounded-2xl bg-white/70 backdrop-blur border border-slate-200/70 p-5">
            <div className="flex items-center gap-2 text-slate-500 mb-1.5">
              <CalendarDays className="w-4 h-4" strokeWidth={2.2} />
              <p className="text-xs font-bold uppercase tracking-[0.14em]">Durasi</p>
            </div>
            <p className="font-heading font-extrabold text-3xl md:text-4xl text-slate-900" data-testid="calc-duration">
              18 Bulan
            </p>
            <p className="text-sm font-semibold text-slate-500 mt-0.5">
              (±<CountUp to={548} /> Hari)
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 p-6 md:p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 hero-grid-light pointer-events-none" />
          <p className="relative text-blue-100 text-xs md:text-sm font-bold uppercase tracking-[0.18em] mb-2 flex items-center justify-center gap-1.5">
            <Sparkles className="w-4 h-4" /> Biaya Per Hari
          </p>
          <p className="relative font-heading font-extrabold text-4xl md:text-6xl text-white" data-testid="calc-per-day">
            ≈ <CountUp to={145} prefix="Rp" duration={1.8} /> <span className="text-2xl md:text-3xl font-bold text-blue-100">/ Hari</span>
          </p>
          <p className="relative mt-3 text-blue-50/90 text-sm md:text-base font-medium">
            Kurang dari Rp200 per hari untuk menikmati seluruh fitur premium Google Gemini.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);
